/**
 * @function getCreditCardType
 * @desc Returns the credit card type object based on the input.  The object
 * contains the type as well as the length which can be used for card validation
 * @param {string} cardNumber - credit card number
 *
 * @returns {object} returns and object with a type and lengths.
 */

const VISA: string = 'visa';
const MASTERCARD = 'master-card';
const AMERICAN_EXPRESS = 'american-express';
const DISCOVER = 'discover';
const EXPRESS = 'express';
const CVV = 'CVV';
const CID = 'CID';
const CVC = 'CVC';
const testOrder = [EXPRESS, VISA, MASTERCARD, DISCOVER, AMERICAN_EXPRESS];

interface CreditCard {
  niceType: string;
  type: string;
  prefixPattern: RegExp;
  exactPattern: RegExp;
  gaps: number[];
  lengths: number;
  code: {
    name: string | null;
    size: number | null;
  };
  isAmex?: boolean;
}

interface CreditCards {
  visa: CreditCard;
  'master-card': CreditCard;
  'american-express': CreditCard;
  discover: CreditCard;
  express: CreditCard;
}

const types: CreditCards = {
  visa: {
    niceType: 'Visa',
    type: VISA,
    prefixPattern: /^4$/,
    exactPattern: /^4\d*$/,
    gaps: [4, 8, 12],
    lengths: 16,
    code: {
      name: CVV,
      size: 3,
    },
  },
  'master-card': {
    niceType: 'MasterCard',
    type: MASTERCARD,
    prefixPattern: /^(5|5[1-6]|2|22|222|222[1-9]|2[3-6]|27|27[0-2]|2720)$/,
    exactPattern: /^(5[1-6]|222[1-9]|2[3-6]|27[0-1]|2720)\d*$/,
    gaps: [4, 8, 12],
    lengths: 16,
    code: {
      name: CVC,
      size: 3,
    },
  },
  'american-express': {
    niceType: 'American Express',
    type: AMERICAN_EXPRESS,
    prefixPattern: /^(3|34|37)$/,
    exactPattern: /^3[47]\d*$/,
    isAmex: true,
    gaps: [4, 10],
    lengths: 15,
    code: {
      name: CID,
      size: 4,
    },
  },
  discover: {
    niceType: 'Discover',
    type: DISCOVER,
    prefixPattern: /^(6|60|601|6011|65|64|64[4-9])$/,
    exactPattern: /^(6011|65|64[4-9])\d*$/,
    gaps: [4, 8, 12],
    lengths: 16,
    code: {
      name: CID,
      size: 3,
    },
  },
  express: {
    niceType: 'Express',
    type: EXPRESS,
    prefixPattern:
      /^(3[0-9][0-9]|4([5-9][6-9]|[6-9][0-9])|50[0-9]|90[3-9])[0-9][0-9][0-9][0-9][0-9]\d$/,
    exactPattern:
      /^(3[0-9][0-9]|4([5-9][6-9]|[6-9][0-9])|50[0-9]|90[3-9])[0-9][0-9][0-9][0-9][0-9]\d$/,
    gaps: [],
    lengths: 9,
    code: {
      name: null,
      size: null,
    },
  },
};

const clone = (x: CreditCard): CreditCard => {
  const prefixPattern = x.prefixPattern.source;
  const exactPattern = x.exactPattern.source;
  const dupe = JSON.parse(JSON.stringify(x));
  dupe.prefixPattern = prefixPattern;
  dupe.exactPattern = exactPattern;

  return dupe;
};

function creditCardType(cardNumber: string | undefined | null = undefined): {
  type: string | null;
  lengths: number | null;
} {
  let type: string = VISA;
  let value: CreditCard = {
    niceType: 'Visa',
    type: VISA,
    prefixPattern: /^4$/,
    exactPattern: /^4\d*$/,
    gaps: [4, 8, 12],
    lengths: 16,
    code: {
      name: CVV,
      size: 3,
    },
  };
  let i;
  const prefixResults = [];
  const exactResults = [];

  if (!(typeof cardNumber === 'string')) {
    return { type: null, lengths: null };
  }

  for (i = 0; i < testOrder.length; i += 1) {
    type = testOrder[i];
    value = types[type as keyof typeof types];
    if (cardNumber.length === 0) {
      prefixResults.push(clone(value));
    }

    if (value.exactPattern.test(cardNumber)) {
      exactResults.push(clone(value));
    } else if (value.prefixPattern.test(cardNumber)) {
      prefixResults.push(clone(value));
    }
  }

  const results: CreditCard[] | undefined =
    exactResults.length > 0 ? exactResults : prefixResults;

  // take the array, and grab the first type or just return null
  if (results !== undefined && results.length > 0 && results.length < 3) {
    return results[0];
  }
  // return nulls to be checked against
  return { type: null, lengths: null };
}

creditCardType.types = {
  EXPRESS,
  VISA,
  MASTERCARD,
  AMERICAN_EXPRESS,
  DISCOVER,
};

export default creditCardType;
